import config from '../config/config';

// 閾値 / タイミング
const POSITION_THRESHOLD = config.map.thresholds.position;
const DEBOUNCE_WAIT_MS = config.locationInfo.debounceWaitMs;
const ADDRESS_UPDATE_INTERVAL_MS = config.locationInfo.updateIntervalMs;

// タイムアウト / メッセージ
const API_REQUEST_TIMEOUT_MS = config.locationInfo.apiRequestTimeoutMs;
const ADDRESS_ERROR_MESSAGE = config.locationInfo.messages.addressError;
const UNEXPECTED_DATA_STRUCTURE_ERROR_MESSAGE = config.locationInfo.messages.unexpectedData;

// JSONP
const JSONP_RANDOM_MAX = config.jsonp.randomMax;
const JSONP_CALLBACK_PREFIX = config.jsonp.callbackPrefix;

// 距離単位
const UNIT_METER = config.common.unit.meter;
const UNIT_KILOMETER = config.common.unit.kilometer;
const METER_PER_KM = config.common.conversions.metersPerKm;
const DISTANCE_DECIMAL_PLACES = config.locationInfo.distance.decimalPlaces;

// DOM定数
const SCRIPT_TAG = config.common.domTags.script;

// デフォルト結果
const EMPTY_LOCATION_RESULT = { stations: [], address: '' };

// ランタイム変数
let latestRequestId = 0;
let lastAddressUpdateTime = 0;
let latestStationsRequestId = 0;

// JSONP コールバック名を衝突しにくく生成するヘルパー
let __jsonpCounter = 0;
function createJsonpCallbackName() {
  __jsonpCounter += 1;
  const rnd = Math.floor(Math.random() * JSONP_RANDOM_MAX);
  return `${JSONP_CALLBACK_PREFIX}${Date.now()}_${rnd}_${__jsonpCounter}`;
}

/**
 * 位置情報（住所・最寄駅）の取得
 * - 位置変化が閾値以上の場合のみ新しいデータを取得
 * - n秒以内の連続呼び出しは空データを返す（API呼び出し制限）
 */
export const updateLocationInfo = createSmartDebounce(
  (lat, lon) => {
    const now = Date.now();
    if (now - lastAddressUpdateTime < ADDRESS_UPDATE_INTERVAL_MS) {
      return Promise.resolve(EMPTY_LOCATION_RESULT);
    }

    return Promise.all([
      updateNearestStations(lat, lon),
      updateAddress(lon, lat),
    ]).then(([stations, address]) => {

      // 住所取得成功時のみタイムスタンプを更新（失敗時は次回すぐリトライ可能）
      if (address && address !== ADDRESS_ERROR_MESSAGE && address !== UNEXPECTED_DATA_STRUCTURE_ERROR_MESSAGE) {
        lastAddressUpdateTime = Date.now();
      }
      return { stations, address };
    });
  },
  DEBOUNCE_WAIT_MS,
  POSITION_THRESHOLD
);

/**
 * 住所を取得
 */
function updateAddress(lon, lat) {
  const currentRequestId = ++latestRequestId;

  return fetchAddress(lon, lat)
    .then(data => {
      if (currentRequestId !== latestRequestId) {
        return '';
      }
      if (!data?.value?.body?.where?.full?.address) {
        return ADDRESS_ERROR_MESSAGE;
      }
      return data.value.body.where.full.address;
    })
}

/**
 * 最寄駅を取得
 */
function updateNearestStations(lat, lon) {
  const currentRequestId = ++latestStationsRequestId;
  return fetchNearestStations(lat, lon)
    .then(stations => {
      // 古いレスポンスは無視する
      if (currentRequestId !== latestStationsRequestId) {
        return [];
      }
      return stations.map(station => ({
        name: station.st_name,
        distance: formatDistance(parseFloat(station.distance))
      }));
    });
}

/**
 * デバウンス処理（前回の結果を再利用）
 * 連続呼び出しを制御し、不要な API リクエストを削減する
 * - 位置変化が閾値未満の場合: 前回の結果を再利用
 * - wait ms 以内の連続呼び出し: デバウンス（最後の呼び出しのみ実行）
 * - wait ms 経過後 & 位置変化あり: 即座に実行
 *
 * @param {Function} func - 実行する関数
 * @param {number} wait - デバウンス待機時間（ミリ秒）
 * @param {number} threshold - 位置変化の閾値（度数）
 * @returns {Function} デバウンス処理が適用された関数
 */
function createSmartDebounce(func, wait, threshold) {
  let timeout;        // デバウンスタイマー
  let lastExec = 0;   // 最後に実行した時刻
  let lastPos = null; // 前回の位置
  let lastResult = null; // 前回の実行結果

  return function (lat, lon) {
    return new Promise((resolve) => {
      const now = Date.now();
      // 位置変化を検出（初回または閾値以上移動した場合は true）
      const hasPositionChanged = !lastPos ||
        Math.abs(lat - lastPos.lat) > threshold ||
        Math.abs(lon - lastPos.lon) > threshold;

      clearTimeout(timeout);

      if (now - lastExec >= wait && hasPositionChanged) {
        // ケース1: 待機時間経過 & 位置変化あり → 即座に実行
        executeFunction();
      } else {
        // ケース2: 待機時間内 or 位置変化なし → デバウンス or 前回結果を再利用
        timeout = setTimeout(() => {
          if (hasPositionChanged) {
            // 待機後に位置変化あり → 実行
            executeFunction();
          } else if (lastResult) {
            // 位置変化なし → 前回の結果を安全に解決して返す
            Promise.resolve(lastResult)
              .then((value) => {
                resolve(value);
              })
              .catch(() => {
                resolve(EMPTY_LOCATION_RESULT);
              });
          } else {
            // 前回結果なし（初回など） → 空データを返す
            resolve(EMPTY_LOCATION_RESULT);
          }
        }, wait);
      }

      function executeFunction() {
        // func が同期例外を投げても安全にフォールバックする
        try {
          lastResult = func(lat, lon);
        } catch (err) {
          lastResult = Promise.resolve(EMPTY_LOCATION_RESULT);
        }
        lastExec = now;
        lastPos = { lat, lon };
        Promise.resolve(lastResult)
          .then((value) => {
            resolve(value);
          })
          .catch(() => {
            resolve(EMPTY_LOCATION_RESULT);
          });
      }
    });
  };
}

/**
 * 距離を「○○m」「○.○km」形式に変換
 */
export function formatDistance(distance) {
  const n = Number(distance);
  if (!Number.isFinite(n)) {
    return '';
  }

  if (n >= METER_PER_KM) {
    // km表示（小数桁は設定に従う）
    const km = (n / METER_PER_KM).toFixed(DISTANCE_DECIMAL_PLACES);
    return km + UNIT_KILOMETER;
  }
  // m表示
  const m = Math.round(n);
  return m + UNIT_METER;
}

/**
 * 住所データをJSONPで取得
 */
export function fetchAddress(lon, lat) {

  return new Promise((resolve) => {
    const callbackName = createJsonpCallbackName();
    const timestamp = new Date().getTime();
    const script = document.createElement(SCRIPT_TAG);

    const url = config.api.reverseGeocoder(lon, lat, callbackName, timestamp);
    script.src = url;

    let timeoutId;
    const cleanup = () => {
      clearTimeout(timeoutId);
      delete window[callbackName];
      if (script.parentNode) {
        document.body.removeChild(script);
      }
    };

    window[callbackName] = (data) => {
      cleanup();
      resolve(data);
    };

    script.onerror = () => {
      cleanup();
      resolve(null);
    };

    document.body.appendChild(script);

    timeoutId = setTimeout(() => {
      cleanup();
      resolve(null);
    }, API_REQUEST_TIMEOUT_MS);
  });
}

/**
 * 最寄駅データをJSONPで取得
 */
export function fetchNearestStations(lat, lon) {
  return new Promise((resolve) => {
    const callbackName = createJsonpCallbackName();
    const script = document.createElement(SCRIPT_TAG);
    const url = config.api.stationPoiCircle(lon, lat, callbackName);
    script.src = url;

    let timeoutId;
    const cleanup = () => {
      clearTimeout(timeoutId);
      delete window[callbackName];
      if (script.parentNode) {
        document.body.removeChild(script);
      }
    };

    window[callbackName] = (data) => {
      cleanup();
      if (data.mbml?.PoiList?.Poi) {
        const stations = Array.isArray(data.mbml.PoiList.Poi) ?
          data.mbml.PoiList.Poi : [data.mbml.PoiList.Poi];
        resolve(stations);
      } else {
        resolve([]);
      }
    };

    script.onerror = () => {
      cleanup();
      resolve([]);
    };

    document.body.appendChild(script);

    timeoutId = setTimeout(() => {
      cleanup();
      resolve([]);
    }, API_REQUEST_TIMEOUT_MS);
  });
}
